/**
 * Environment detection utilities for WillPower Store
 * Handles development mode and Bogus Gateway configuration
 */

import { logger } from "../../../../server/src/lib/logger";

/**
 * Check if the application is running in development mode
 */
export function isDevelopment(): boolean {
  return process.env.NODE_ENV === "development";
}

/**
 * Check if the application is running in production mode
 */
export function isProduction(): boolean {
  return process.env.NODE_ENV === "production";
}

/**
 * Check if Bogus Gateway testing is enabled
 * In development mode, Bogus Gateway is enabled by default
 */
export function isBogusGatewayEnabled(): boolean {
  if (!isDevelopment()) {
    return false;
  }

  // Allow explicit disable in development if needed
  const explicitDisable =
    process.env.NEXT_PUBLIC_DISABLE_BOGUS_GATEWAY === "true";
  return !explicitDisable;
}

/**
 * Get the current environment name
 */
export function getEnvironment(): "development" | "production" | "test" {
  const env = process.env.NODE_ENV;
  if (env === "development" || env === "production" || env === "test") {
    return env;
  }
  return "development"; // fallback
}

/**
 * Check if we should show development UI features
 */
export function shouldShowDevelopmentUI(): boolean {
  return isDevelopment() && isBogusGatewayEnabled();
}

/**
 * Get environment-specific configuration
 */
export function getEnvironmentConfig() {
  return {
    isDevelopment: isDevelopment(),
    isProduction: isProduction(),
    isBogusGatewayEnabled: isBogusGatewayEnabled(),
    shouldShowDevelopmentUI: shouldShowDevelopmentUI(),
    environment: getEnvironment(),
  };
}

/**
 * Log development-only messages
 */
export function devLog(message: string, ...args: any[]): void {
  if (isDevelopment()) {
    logger.debug(message, ...args);
  }
}

/**
 * Log Bogus Gateway specific messages
 */
export function bogusLog(message: string, ...args: any[]): void {
  if (isBogusGatewayEnabled()) {
    logger.debug(message, ...args);
  }
}

// Top-level regex constant to avoid recompilation on every function call
const TRAILING_SLASH_REGEX = /\/$/;

/**
 * Get the base URL for the application
 * Used for sitemap generation and structured data absolute URLs
 * Detects preview vs production environments automatically
 * @returns Base URL (e.g., "https://www.willpowerpt.store", "https://preview-web.willpowerpt.store", or "http://localhost:3001")
 */
export function getBaseUrl(): string {
  // In production, detect preview vs production based on hostname
  if (isProduction()) {
    const productionUrl = process.env.NEXTAUTH_URL;
    if (productionUrl) {
      // Remove trailing slash if present (use top-level regex)
      return productionUrl.replace(TRAILING_SLASH_REGEX, "");
    }

    // Fallback: Detect from origin if available (server-side rendering)
    if (typeof window === "undefined") {
      // Server-side: Check common Vercel preview URL patterns
      const host = process.env.VERCEL_URL;
      if (host) {
        const protocol =
          process.env.VERCEL_ENV === "production" ? "https" : "https";
        return `${protocol}://${host}`;
      }
    }

    // Last resort fallback
    logger.warn(
      "Could not determine production URL, falling back to localhost:3001"
    );
    return "http://localhost:3001";
  }

  // In development, default to localhost:3001
  return "http://localhost:3001";
}
